import React, { useEffect, useMemo, useRef, useState } from 'react';
import { normalizarTexto } from '../../../utils/texto';
import { createPortal } from 'react-dom';
import { Search, X, UserRound } from 'lucide-react';
import { iniciales } from '../utils/helpers';
import { SHADOW } from '../utils/uiTokens';

/**
 * Selector compacto de responsable para filas de tabla.
 * - value: empleado_id o null
 * - rolTexto: responsable descriptivo de la plantilla ("Analista DP"), se
 *   muestra cuando no hay persona elegida para que se sepa a quien buscar.
 * El dropdown va en portal para no quedar recortado por tablas con overflow.
 */
const EmpleadoPicker = ({ value, onChange, empleados, rolTexto, disabled = false, placeholder = 'Sin asignar' }) => {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const actual = useMemo(() => empleados.find((e) => e.id === value) || null, [empleados, value]);

  const filtrados = useMemo(() => {
    const t = normalizarTexto(q).trim();
    // Sin busqueda y con rol sugerido: prioriza quienes tienen ese cargo.
    const base = t
      ? empleados.filter(
          (e) => normalizarTexto(e.nombre).includes(t) || normalizarTexto(e.cargo).includes(t)
        )
      : rolTexto
        ? [
            ...empleados.filter((e) => e.cargo && e.cargo.toLowerCase().includes(rolTexto.toLowerCase())),
            ...empleados,
          ]
        : empleados;
    const vistos = new Set();
    return base.filter((e) => (vistos.has(e.id) ? false : vistos.add(e.id))).slice(0, 40);
  }, [empleados, q, rolTexto]);

  const abrir = () => {
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const alto = 300;
      const abajo = window.innerHeight - r.bottom > alto;
      setPos({
        left: Math.min(r.left, window.innerWidth - 280),
        top: abajo ? r.bottom + 4 : Math.max(8, r.top - alto - 4),
      });
    }
    setQ('');
    setAbierto(true);
  };

  useEffect(() => {
    if (!abierto) return;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setAbierto(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setAbierto(false);
      }
    };
    const onScroll = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      setAbierto(false);
    };
    document.addEventListener('mousedown', onDown);
    // En window (captura) para ganarle al modal que lo contiene: Esc cierra
    // solo el dropdown.
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [abierto]);

  const elegir = (id) => {
    onChange(id);
    setAbierto(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={abrir}
        disabled={disabled}
        title={actual ? actual.nombre : rolTexto ? `Rol sugerido: ${rolTexto}` : placeholder}
        className={`group w-full min-w-0 flex items-center gap-1.5 px-1.5 py-1 rounded-md text-left text-xs transition-colors ${
          disabled ? 'cursor-default' : 'hover:bg-slate-100'
        }`}
      >
        {actual ? (
          <>
            <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-800 text-[9px] font-semibold flex items-center justify-center flex-shrink-0">
              {iniciales(actual.nombre)}
            </span>
            <span className="truncate text-slate-800">{actual.nombre}</span>
          </>
        ) : (
          <>
            <span className="w-5 h-5 rounded-full border border-dashed border-slate-300 flex items-center justify-center flex-shrink-0">
              <UserRound size={10} className="text-slate-400" />
            </span>
            <span className={`truncate ${rolTexto ? 'text-slate-500 italic' : 'text-slate-400'}`}>
              {rolTexto || placeholder}
            </span>
          </>
        )}
      </button>

      {abierto && pos && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: 272 }}
          className={`z-[200] bg-white rounded-lg ${SHADOW.dropdown} overflow-hidden`}
        >
          <div className="relative border-b border-slate-100">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o cargo"
              className="w-full pl-8 pr-3 py-2 text-sm placeholder-slate-400 focus:outline-none"
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {value && (
              <button
                type="button"
                onClick={() => elegir(null)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50"
              >
                <X size={12} /> Quitar responsable
              </button>
            )}
            {filtrados.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-400">Nadie coincide con "{q}"</div>
            ) : (
              filtrados.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => elegir(e.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 ${
                    e.id === value ? 'bg-cyan-50' : ''
                  }`}
                >
                  <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                    {iniciales(e.nombre)}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm text-slate-800 truncate">{e.nombre}</span>
                    {e.cargo && <span className="block text-[11px] text-slate-400 truncate">{e.cargo}</span>}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default EmpleadoPicker;
